import { formatWorkoutNumber } from '../utils/workoutNumberFormat';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { FitRecordedMetadata } from '../utils/fitRecordedMetrics';
import '../styles/workout-physiology.css';

export const focusedDeviceMetricNames = new Set([
  'power_balance_left', 'power_balance_right', 'left_right_balance',
  'left_torque_effectiveness', 'right_torque_effectiveness',
  'left_pedal_smoothness', 'right_pedal_smoothness',
  'total_training_effect', 'total_anaerobic_training_effect', 'training_load_peak',
  'total_calories', 'metabolic_calories', 'calories_consumed', 'fluid_consumed',
  'estimated_sweat_loss', 'est_sweat_loss', 'sweat_loss',
]);

export function WorkoutPhysiologyPanels({ snapshot, mode }: {
  snapshot?: FitRecordedMetadata | null; mode: 'summary' | 'analysis';
}) {
  const { t, i18n } = useTranslation();
  const id = useId().replace(/:/g, '');
  const metrics = snapshot?.metrics ?? [];
  const value = (...names: string[]) => {
    for (const name of names) {
      const metric = metrics.find(item => item.name === name && typeof item.avg === 'number' && Number.isFinite(item.avg));
      if (metric) return metric.avg;
    }
    return null;
  };
  const f = (n: number | null, decimals = 0) => n == null ? '—' : formatWorkoutNumber(n, i18n.resolvedLanguage ?? i18n.language, { maximumFractionDigits: decimals });
  const label = (key: string) => t(`workoutInsights.${key}`);
  const clamp = (n: number, max = 100) => Math.max(0, Math.min(max, n));
  const aerobic = value('total_training_effect');
  const anaerobic = value('total_anaerobic_training_effect');

  if (mode === 'summary') {
    if (aerobic == null && anaerobic == null) return null;
    return <section className="workout-insights workout-effects" dir={i18n.dir()} aria-labelledby={`${id}-effects`}>
      <header><h3 id={`${id}-effects`}>{label('trainingEffect')}</h3></header>
      <div className="workout-effects__gauges">{([['aerobic', aerobic], ['anaerobic', anaerobic]] as const).filter(([, n]) => n != null).map(([key, n]) => <div className={`workout-effects__gauge workout-effects__gauge--${key}`} key={key}>
        <svg viewBox="0 0 120 80" aria-hidden="true"><path className="workout-effects__track" d="M 15 65 A 45 45 0 0 1 105 65" pathLength="100"/><path className="workout-effects__arc" d="M 15 65 A 45 45 0 0 1 105 65" pathLength="100" strokeDasharray={`${n == null ? 0 : clamp(n, 5) * 20} 100`}/></svg>
        <div className="workout-effects__number" dir="ltr"><bdi>{n == null ? '—' : formatWorkoutNumber(clamp(n, 5) / 5, i18n.resolvedLanguage ?? i18n.language, { style: 'percent', maximumFractionDigits: 0 })}</bdi></div><strong>{label(key)}</strong>
      </div>)}</div>
    </section>;
  }

  const pairs = [
    { key: 'balance', left: value('power_balance_left'), right: value('power_balance_right') },
    { key: 'torque', left: value('left_torque_effectiveness'), right: value('right_torque_effectiveness') },
    { key: 'smoothness', left: value('left_pedal_smoothness'), right: value('right_pedal_smoothness') },
  ];
  const balance = value('left_right_balance');
  if (pairs[0].left == null && pairs[0].right == null && balance != null && balance >= 0 && balance <= 100) {
    pairs[0].left = balance; pairs[0].right = 100 - balance;
  }
  const total = value('total_calories');
  const resting = value('metabolic_calories');
  const consumed = value('calories_consumed');
  const active = total != null && resting != null && total >= resting ? total - resting : null;
  const sweat = value('estimated_sweat_loss', 'est_sweat_loss', 'sweat_loss');
  const fluid = value('fluid_consumed');
  const net = sweat != null && fluid != null ? fluid - sweat : null;
  const nutrition = [['restingCalories', resting], ['activeCalories', active], ['totalCalories', total], ['caloriesConsumed', consumed], ['caloriesNet', consumed != null && total != null ? consumed - total : null]] as const;
  const nutritionRows = nutrition.filter(([, n]) => n != null);
  const hydrationRows = ([['sweatLoss', sweat], ['fluidConsumed', fluid], ['fluidNet', net]] as const).filter(([, n]) => n != null);
  const showHydrationGraphic = sweat != null && sweat > 0 && fluid != null;
  if (!pairs.some(pair => pair.left != null || pair.right != null) && !nutritionRows.length && !hydrationRows.length) return null;
  return <div className="workout-insights-stack" dir={i18n.dir()}>
    {pairs.some(pair => pair.left != null || pair.right != null) && <section className="workout-insights" aria-labelledby={`${id}-cycling`}>
      <header><span className="workout-insights__eyebrow">{label('deviceRecorded')}</span><h3 id={`${id}-cycling`}>{label('cyclingDynamics')}</h3></header>
      <div className="workout-pedal-metrics">
        {pairs.map(pair => pair.left == null && pair.right == null ? null : <div className="workout-pedal-metric" key={pair.key}>
          <h4>{label(pair.key)}</h4>
          <div className="workout-pedal-metric__body" dir="ltr">
            <div className="workout-pedal-metric__value workout-pedal-metric__value--left"><span>{label('left')}</span><bdi>{pair.left == null ? '—' : `${f(pair.left, 1)}%`}</bdi></div>
            <div className="workout-pedal-dial" aria-hidden="true">
              <svg viewBox="0 0 144 144">
                {[pair.left, pair.right].map((n, side) => <g key={side} className={`workout-pedal-dial__ring workout-pedal-dial__ring--${side === 0 ? 'left' : 'right'}`}>
                  <path className="workout-pedal-dial__track" d={side === 0 ? "M72 13A59 59 0 0 0 72 131" : "M72 13A59 59 0 0 1 72 131"} />
                  <path className="workout-pedal-dial__arc" d={side === 0 ? "M72 13A59 59 0 0 0 72 131" : "M72 13A59 59 0 0 1 72 131"} pathLength="100" strokeDasharray={`${n == null ? 0 : clamp(n)} 100`} />
                </g>)}
              </svg>
            </div>
            <div className="workout-pedal-metric__value workout-pedal-metric__value--right"><span>{label('right')}</span><bdi>{pair.right == null ? '—' : `${f(pair.right, 1)}%`}</bdi></div>
          </div>
        </div>)}
      </div>
    </section>}
    {(nutritionRows.length > 0 || hydrationRows.length > 0) && <section className="workout-insights" aria-labelledby={`${id}-nutrition`}>
      <header><h3 id={`${id}-nutrition`}>{label(nutritionRows.length && hydrationRows.length ? 'nutritionHydration' : nutritionRows.length ? 'nutrition' : 'hydration')}</h3></header>
      {nutritionRows.length > 0 && <dl className="workout-nutrition">{nutritionRows.map(([key, n]) => <div key={key} data-metric={key}><dt>{label(key)}</dt><dd><bdi>{f(n)}</bdi><small>{label('kcal')}</small></dd></div>)}</dl>}
      {hydrationRows.length > 0 && <div className={`workout-hydration${nutritionRows.length ? '' : ' workout-hydration--only'}`}>
        {showHydrationGraphic && <div className="workout-hydration__drop" aria-hidden="true"><div className="workout-hydration__sweat">{[0, 1, 2].map(n => <svg key={n} viewBox="0 0 16 22" style={{ animationDelay: `${n * 40}ms` }}><path d="M8 1C6 5 1 10 1 14a7 7 0 0 0 14 0C15 10 10 5 8 1Z"/></svg>)}</div><svg viewBox="0 0 90 112"><defs><clipPath id={`${id}-drop`}><path d="M45 5C35 24 12 49 12 69a33 33 0 0 0 66 0C78 49 55 24 45 5Z"/></clipPath></defs><path className="workout-hydration__outline" d="M45 5C35 24 12 49 12 69a33 33 0 0 0 66 0C78 49 55 24 45 5Z"/><g clipPath={`url(#${id}-drop)`}><g className="workout-hydration__fill" style={{ transform: `translateY(${102 - clamp(fluid / sweat * 100) * .97}px)` }}><path d="M0 0Q22-8 45 0T90 0V112H0Z"/></g></g></svg></div>}
        <dl>{hydrationRows.map(([key, n]) => <div key={key}><dt>{label(key)}</dt><dd><bdi>{f(n)}</bdi><small>{label('ml')}</small></dd></div>)}</dl>
      </div>}
    </section>}
  </div>;
}
